import { ListenableSubscribable, Subscription } from '@benzinga/subscribable';
import { SafePromise } from '@benzinga/safe-await';
import { LAYOUT_VERSION, Layout, LayoutChangeType, LayoutList, LayoutListItem, UpdateLayout } from './entities';
import { LayoutRequest, LayoutRequestEvent } from './request';
import { AuthenticationManager, Session } from '@benzinga/session';
import { LayoutStore, LayoutStoreEvent } from './store';
import { oneRequestAtATime, oneRequestAtATimeMultipleArgs } from '@benzinga/utils';
import { compare } from 'fast-json-patch';

export type LayoutManagerEvent = LayoutRequestEvent | LayoutStoreEvent;

const FLUSH_TIMEOUT = 5000;
export class LayoutManager extends ListenableSubscribable<LayoutManagerEvent> {
  private request: LayoutRequest;
  private requestSubscription?: Subscription<LayoutRequest>;
  private store: LayoutStore;
  private storeSubscription?: Subscription<LayoutStore>;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private lastLayoutAction: Omit<LayoutChangeType, 'add'> = LayoutChangeType.REPLACE;

  private getDelayedLayoutsList = oneRequestAtATime(async (): SafePromise<LayoutList | undefined> => {
    const layouts = await this.request.getLayoutsList();
    if (layouts.err) {
      return { err: layouts.err };
    } else {
      this.store.updateLayoutsList(layouts.ok ?? []);
    }
    return { ok: this.store.getLayoutsList() };
  });

  private saveLayoutInternal = oneRequestAtATimeMultipleArgs(
    async (layout: UpdateLayout): Promise<Layout | undefined> => {
      const layoutKey = Object.keys(layout)[0];
      if (!layoutKey) return;

      const layoutChanges = layout[layoutKey];
      if (!layoutChanges) return;

      // force fetch from BE
      const fetched = await this.getLayout(layoutChanges?.uuid ?? 'placeholder', true);
      if (fetched.err) return;

      const mergedLayout = this.mergeLayout(fetched.ok, layoutChanges);
      await this.compareDiffAndStoreLayout(fetched.ok, mergedLayout);
      return this.store.getCachedLayout();
    },
  );

  private getLayoutInternal = oneRequestAtATimeMultipleArgs(
    async (uuid: string, force?: boolean): SafePromise<Layout> => {
      const cachedLayout = this.store.getCachedLayout();
      if ((uuid && force) || !cachedLayout || cachedLayout.uuid !== uuid) {
        const response = await this.request.getLayout(uuid);
        if (response.err) {
          return { err: response.err };
        }
        return { ok: response.ok };
      }
      return { ok: cachedLayout };
    },
  );

  constructor(session: Session) {
    super();
    this.request = new LayoutRequest(session);
    this.store = new LayoutStore();

    const authenticationManager = session.getManager(AuthenticationManager);
    this.getLayoutsList(true);

    authenticationManager.subscribe(event => {
      switch (event.type) {
        case 'authentication:session_update':
          this.store.clearLayouts();
      }
    });
  }

  public static getName = () => 'benzinga-layout';

  public getCachedLayout = (): Layout | undefined => this.store.getCachedLayout();

  public getLayout = async (uuid: string, force?: boolean): SafePromise<Layout> =>
    await this.getLayoutInternal(uuid, force);

  // avoid using this.
  public getStoredLayoutsList = (): LayoutList | undefined => {
    return this.store.getLayoutsList();
  };

  public getLayoutsList = async (force?: boolean): SafePromise<LayoutList | undefined> => {
    if (force || this.store.shouldWeFetchLayout()) {
      return this.getDelayedLayoutsList();
    } else {
      return { ok: this.store.getLayoutsList() };
    }
  };

  public newLayout = async (name?: string, layout?: Layout['data']): Promise<Layout | undefined> => {
    const response = await this.request.postLayout(name ?? 'Untitled', layout ?? { layoutVersion: LAYOUT_VERSION });
    return response.ok;
  };

  public batchLayoutSave = (layoutAction: UpdateLayout): void => {
    const layoutDelta = layoutAction.remove ?? layoutAction.replace ?? undefined;
    if (!layoutDelta) return;

    this.store.updateLayout(layoutDelta);
    this.lastLayoutAction = layoutAction.remove ? LayoutChangeType.REMOVE : LayoutChangeType.REPLACE;

    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
    }

    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      const cachedLayout = this.getCachedLayout();
      this.saveLayout({
        [this.lastLayoutAction === LayoutChangeType.REMOVE ? 'remove' : 'replace']: cachedLayout ?? undefined,
      });
      this.lastLayoutAction = LayoutChangeType.REPLACE;
    }, FLUSH_TIMEOUT);
  };
  public saveLayout = async (layout: UpdateLayout): Promise<Layout | undefined> =>
    await this.saveLayoutInternal(layout);

  public deleteLayout = async (layoutId: Layout['uuid']): SafePromise<undefined> => {
    const res = await this.request.deleteLayout(layoutId);
    if (res.ok) {
      this.store.removeLayout({ uuid: layoutId } as LayoutListItem);
    }
    return res;
  };

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
    this.storeSubscription = this.store.listen(event => this.dispatch(event));
  }

  protected onZeroLayout = (): void => {
    this.requestSubscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
  };

  private sanitizeLayout = <T>(obj: T): T =>
    JSON.parse(
      JSON.stringify(obj, (_key, value) => {
        if (typeof value === 'function' || typeof value === 'undefined') {
          return undefined;
        }
        return value;
      }),
    );

  private mergeLayout = (base: Layout, changes: Partial<Layout>): Layout => {
    return {
      ...base,
      ...changes,
      data: {
        ...base.data,
        ...changes.data,
      },
    };
  };

  private compareDiffAndStoreLayout = async (cachedLayout: Layout, currentLayout: Layout) => {
    // calculating layout data delta
    const layoutDeltaChange = compare(this.sanitizeLayout(cachedLayout), this.sanitizeLayout(currentLayout));
    // checking if there are any changes
    if (layoutDeltaChange.length === 0) {
      return;
    }
    const validPatch = layoutDeltaChange.filter(
      operation => !['add', 'replace'].includes(operation.op) || 'value' in operation,
    );

    if (validPatch.length === 0) return;
    const patchResponse = await this.request.patchLayout(currentLayout.uuid, validPatch);

    if (patchResponse.err) {
      // If PATCH fails, fall back to PUT with the complete layout
      await this.request.putLayout(currentLayout.uuid, currentLayout);
    }
  };
}

export const countLayoutItems = (layout: Layout) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore old schema
  const { userWorkspaces, workspaceIds } = layout.data.workspaces;
  // adapting to new schema
  const workspacesCount = (userWorkspaces ?? workspaceIds ?? []).length;

  const widgetList =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore old schema
    layout.data.widgets?.list ?? (layout.data.widgetsById ? Object.values(layout.data.widgetsById as WidgetsById) : []);

  // reducing amount of widgets not include chat(very old schema)/stream
  const widgetsCount = widgetList.reduce(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - old schema
    (sum, widget) =>
      widget.widgetId === layout.data.settings.globalChatWidgetId || widget.type === 'chat' ? sum : sum + 1,
    0,
  );

  return { widgetsCount, workspacesCount };
};
